import { WallboxConfigurationModel } from '../models/wallbox-configuration/wallbox-configuration.model'

const PHASE_COUNT = 3

type WallboxPhasePower = Partial<Pick<WallboxConfigurationModel, 'pmPowerL1' | 'pmPowerL2' | 'pmPowerL3'>>
type WallboxPowerLimits = Partial<Pick<WallboxConfigurationModel, 'upperChargePowerLimit' | 'upperDischargePowerLimit' | 'maxPowerPerPhase'>>

const isPositive = (value: number | undefined): value is number => value !== undefined && Number.isFinite(value) && value > 0

/**
 * Sums the signed power of all three phases (W). Negative = discharging (bidi).
 * A missing phase (undefined / NaN from the DTO transformer) counts as 0.
 * Returns undefined when no phase value is available at all (e.g. live data not received yet).
 */
export const getWallboxTotalPower = (power: WallboxPhasePower | undefined): number | undefined => {
  const phases = [power?.pmPowerL1, power?.pmPowerL2, power?.pmPowerL3].filter((value): value is number => Number.isFinite(value))
  if (phases.length === 0) return undefined
  return phases.reduce((sum, value) => sum + value, 0)
}

/**
 * Upper bound (W) of the total power gauge. The gauge shows the absolute power, so it has to fit both charging and discharging.
 * Uses the hardware bounds, not the user configured maxChargePower / maxDischargePower, so the scale does not change with the settings.
 * Priority: max(upperChargePowerLimit, upperDischargePowerLimit) -> maxPowerPerPhase x 3.
 * Missing or non-positive values are ignored. Returns undefined when the wallbox reports no valid limit at all.
 */
export const getWallboxTotalPowerMax = (limits: WallboxPowerLimits | undefined): number | undefined => {
  const chargeLimits = [limits?.upperChargePowerLimit, limits?.upperDischargePowerLimit].filter(isPositive)
  if (chargeLimits.length > 0) return Math.max(...chargeLimits)
  if (isPositive(limits?.maxPowerPerPhase)) return limits.maxPowerPerPhase * PHASE_COUNT
  return undefined
}
